import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, componentAutoReaction, inject, ItemCarousel, NothingHere, StatusCard } from 'Mri.Web.Common';
import * as React from 'react';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { ContractsStore } from '../Contracts/ContractsStore';
import { AppStore } from '../Core/AppStore';
import { UnitsStore } from '../Units/UnitsStore';
import { WeatherStore } from '../Weather/WeatherStore';
import { DisplayScreenSnapshot } from './DisplayScreenSnapshot';
import { DisplaysStore } from './DisplaysStore';
import { DisplayStatsCards } from './DisplayStatsCards';
import { DisplayStatusTopCards } from './DisplayStatusTopCards';
import { DisplayAdditionalStatsCard } from './StatusCards/DisplayAdditionalStatsCard';
import { DisplayContractCard } from './StatusCards/DisplayContractCard';
import { DisplayEventsCard } from './StatusCards/DisplayEventsCard';
import { DisplayTemperaturesCard } from './StatusCards/DisplayTemperaturesCard';
import { DisplayUnitCard } from './StatusCards/DisplayUnitCard';
import { TicketsCard } from './StatusCards/TicketsCard';

export interface IDisplayStatusParams {
    serialNumber: string;
}

export interface IDisplayStatusProps extends RouteComponentProps<IDisplayStatusParams> {}

@autoinject
@observer
export class DisplayStatus extends React.Component<IDisplayStatusProps> {
    public async componentDidMount() {
        componentAutoReaction(this, () => this.props.match.params.serialNumber, this.onLoad);
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                {this.display && (
                    <div className="row px-2">
                        <div className="col-12 px-3">
                            <DisplayStatusTopCards display={this.display} statusPoll={this.statusPoll} />
                        </div>

                        {this.screenSnapshots && this.screenSnapshots?.length > 0 && (
                            <div className={this.cardClass}>
                                <StatusCard title={`PopVu™`}>
                                    <div className="d-flex align-items-center justify-content-evenly h-100 p-2">
                                        {this.screenSnapshots &&
                                            this.screenSnapshots.map(s => (
                                                <DisplayScreenSnapshot
                                                    key={s.uri}
                                                    screenSnapshot={s}
                                                    className="d-inline-block"
                                                />
                                            ))}
                                    </div>
                                </StatusCard>
                            </div>
                        )}

                        {this.infiniteTouchSnapshots &&
                            this.infiniteTouchSnapshots?.length > 0 && ( //TEST
                            <div className={this.cardClass}>
                                <StatusCard title={`InfiniteTouch®`}>
                                    <div className="d-flex align-items-center justify-content-evenly h-100 p-2">
                                        {this.infiniteTouchSnapshots &&
                                                this.infiniteTouchSnapshots.map(s => (
                                                    <DisplayScreenSnapshot
                                                        key={s.uri}
                                                        screenSnapshot={s}
                                                        className="d-inline-block"
                                                    />
                                                ))}
                                    </div>
                                </StatusCard>
                            </div>
                        )}

                        {this.unit && this.unit.displays.length > 1 && (
                            <div className={this.cardClass}>
                                <DisplayUnitCard display={this.display} unit={this.unit} loading={this.loading} />
                            </div>
                        )}

                        <DisplayStatsCards display={this.display} loading={this.loading} showMap={this.showMap} />

                        <div className={this.cardClass}>
                            <TicketsCard display={this.display} loading={this.loading} />
                        </div>

                        <div className={this.cardClass}>
                            <DisplayEventsCard display={this.display} loading={this.loading} />
                        </div>

                        <div className={this.cardClass}>
                            <DisplayContractCard display={this.display} loading={this.loading} />
                        </div>

                        <div className="col-12 text-end text-muted">
                            Weather provided via{' '}
                            <a href="https://openweathermap.org/" className="btn btn-link p-0">
                                OpenWeatherMap
                            </a>
                        </div>
                    </div>
                )}

                <NothingHere
                    empty={!this.display}
                    icon={<BsTv />}
                    loading={this.loading}
                    label="No Data Available"
                    loadingMessage="Loading Display..."
                />
            </div>
        );
    }

    @bind
    @action
    private async onLoad() {
        this.loading = true;

        const serialNumber = this.props.match.params.serialNumber;
        try {
            await this.displaysStore.loadAsync(serialNumber, {
                params: {
                    status: true,
                },
            });

            if (!this.display) throw new Error(`${serialNumber} does not exist`);

            const promises: Promise<any>[] = [];

            if (this.display!.contract) promises.push(this.contractsStore.loadAsync(this.display!.contract, true));

            promises.push(
                this.displaysStore.loadCurrentStatusAsync(this.props.match.params.serialNumber).catch(console.error)
            );
            promises.push(this.weatherStore.loadWeatherAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadOpenTicketsAsync(serialNumber).catch(console.error));
            promises.push(
                this.displaysStore
                    .loadLatestScreenSnapshotAsync(this.props.match.params.serialNumber)
                    .catch(console.error)
            );
            if (!this.display.virtualUnit) {
                promises.push(this.unitsStore.loadAsync(this.display.unitSerialNumber).catch(console.error));
            }

            await Promise.all(promises);

            if (!this.display.virtualUnit) await this.loadUnitDisplaysAsync();
        } catch (e: any) {
            this.appStore.alertHandle.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async loadUnitDisplaysAsync() {
        if (!this.display) return;
        if (this.display.virtualUnit) return;

        const promises: Promise<any>[] = [];
        const unit = this.unitsStore.units.get(this.display.unitSerialNumber);
        const unitDisplays = unit?.displays.filter(d => d != this.display!.serialNumber);

        if (unitDisplays) {
            for (const display of unitDisplays) {
                await this.displaysStore
                    .loadAsync(display, {
                        params: {
                            status: true,
                            contract: true,
                        },
                    })
                    .catch(console.error);
            }
        }

        await Promise.all(promises);
    }

    @computed
    private get cardClass() {
        return 'col-md-6 mb-3 px-2';
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.match.params.serialNumber);
    }

    @computed
    private get screenSnapshots() {
        const snapshots = this.displaysStore.screenSnapshots.get(this.props.match.params.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot);
    }

    @computed
    private get infiniteTouchSnapshots() {
        const snapshots = this.displaysStore.screenSnapshots.get(this.props.match.params.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.TouchHeatmap);
    }

    @computed
    private get activeDisplayStatus() {
        return this.displaysStore.statuses.get(this.props.match.params.serialNumber);
    }

    @computed
    private get showMap() {
        const display = this.display;

        return (
            !!display && !!display.unitLocation && !!display.unitLocation.latitude && !!display.unitLocation.longitude
        );
    }

    @computed
    private get statusPoll() {
        if (this.activeDisplayStatus) return this.activeDisplayStatus.lastKnownPoll.displayStatusPoll;
    }

    @computed
    private get unit() {
        if (this.display) return this.unitsStore.units.get(this.display.unitSerialNumber);
    }

    @observable
    private loading = false;
    @inject
    private weatherStore!: WeatherStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private appStore!: AppStore;
    @inject
    private unitsStore!: UnitsStore;
}
